/**
 * Offline Master Data Service
 * Provides offline-capable master data fetching with IndexedDB caching
 *
 * Features:
 * - 7-day TTL for master data (breeds, species, genders, colors, etc.)
 * - Automatic fallback to cache when offline
 * - Background refresh when online
 */

import { offlineDb } from './db';
import { cacheMasterData, getCachedMasterData, isMasterDataCached } from './offlineStorage';
import { MasterDataKey, MASTER_DATA_KEYS } from './constants';

// Import the original masterDataApi to fetch fresh data
// We'll dynamically import to avoid circular dependencies

const isDevelopment = import.meta.env.DEV;
const API_BASE_URL = isDevelopment
  ? '/gaushala-service'
  : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');

/**
 * Generic API call for master data
 */
async function fetchMasterData<T>(endpoint: string): Promise<T[]> {
  const token = localStorage.getItem('saubhagya_jwt_token');

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Response is not JSON');
  }

  const text = await response.text();
  if (!text) {
    return [];
  }

  return JSON.parse(text);
}

/**
 * Get master data with offline support
 * Returns cached data if offline or if cache is valid
 */
async function getMasterDataWithOfflineSupport<T>(
  key: MasterDataKey,
  endpoint: string,
  forceRefresh: boolean = false
): Promise<T[]> {
  const isOnline = navigator.onLine;

  // If online and force refresh, fetch from server
  if (isOnline && forceRefresh) {
    try {
      const data = await fetchMasterData<T>(endpoint);
      await cacheMasterData(key, data);
      console.log(`[OfflineMasterData] Refreshed ${key} from server: ${data.length} items`);
      return data;
    } catch (error) {
      console.error(`[OfflineMasterData] Failed to refresh ${key}:`, error);
      // Fall through to cache
    }
  }

  // Check cache first
  const cachedData = await getCachedMasterData<T>(key);

  if (cachedData && cachedData.length > 0) {
    console.log(`[OfflineMasterData] Returning cached ${key}: ${cachedData.length} items`);

    // If online, refresh in background (but still return cached immediately)
    if (isOnline && !forceRefresh) {
      // Background refresh
      fetchMasterData<T>(endpoint)
        .then(data => cacheMasterData(key, data))
        .catch(err => console.warn(`[OfflineMasterData] Background refresh failed for ${key}:`, err));
    }

    return cachedData;
  }

  // No cache and offline
  if (!isOnline) {
    console.warn(`[OfflineMasterData] Offline and no cache for ${key}`);
    return [];
  }

  // Online and no cache - fetch from server
  try {
    const data = await fetchMasterData<T>(endpoint);
    await cacheMasterData(key, data);
    console.log(`[OfflineMasterData] Fetched ${key} from server: ${data.length} items`);
    return data;
  } catch (error) {
    console.error(`[OfflineMasterData] Failed to fetch ${key}:`, error);
    return [];
  }
}

// ============== MASTER DATA INTERFACES ==============

export interface Breed {
  id: number;
  name: string;
  description?: string;
  speciesId?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface Species {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Gender {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Color {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Location {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Source {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Ownership {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FeedType {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface InventoryType {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface InventoryUnit {
  id: number;
  unitName: string;
  abbreviation?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ============== OFFLINE MASTER DATA API ==============

export const offlineMasterDataApi = {
  /**
   * Get all breeds with offline support
   */
  async getAllBreeds(forceRefresh: boolean = false): Promise<Breed[]> {
    return getMasterDataWithOfflineSupport<Breed>('breeds', '/api/v1/gaushala/master/breeds', forceRefresh);
  },

  /**
   * Get all species with offline support
   */
  async getAllSpecies(forceRefresh: boolean = false): Promise<Species[]> {
    return getMasterDataWithOfflineSupport<Species>('species', '/api/v1/gaushala/master/species', forceRefresh);
  },

  /**
   * Get all genders with offline support
   */
  async getAllGenders(forceRefresh: boolean = false): Promise<Gender[]> {
    return getMasterDataWithOfflineSupport<Gender>('genders', '/api/v1/gaushala/master/genders', forceRefresh);
  },

  /**
   * Get all colors with offline support
   */
  async getAllColors(forceRefresh: boolean = false): Promise<Color[]> {
    return getMasterDataWithOfflineSupport<Color>('colors', '/api/v1/gaushala/master/colors', forceRefresh);
  },

  /**
   * Get all locations with offline support
   */
  async getAllLocations(forceRefresh: boolean = false): Promise<Location[]> {
    return getMasterDataWithOfflineSupport<Location>('locations', '/api/v1/gaushala/master/locations', forceRefresh);
  },

  /**
   * Get all sources with offline support
   */
  async getAllSources(forceRefresh: boolean = false): Promise<Source[]> {
    return getMasterDataWithOfflineSupport<Source>('sources', '/api/v1/gaushala/master/sources', forceRefresh);
  },

  /**
   * Get all ownership types with offline support
   */
  async getAllOwnership(forceRefresh: boolean = false): Promise<Ownership[]> {
    return getMasterDataWithOfflineSupport<Ownership>('ownership', '/api/v1/gaushala/master/ownership', forceRefresh);
  },

  /**
   * Get all feed types with offline support
   */
  async getAllFeedTypes(forceRefresh: boolean = false): Promise<FeedType[]> {
    return getMasterDataWithOfflineSupport<FeedType>('feedTypes', '/api/v1/gaushala/master/feed-types', forceRefresh);
  },

  /**
   * Get all inventory types with offline support
   */
  async getAllInventoryTypes(forceRefresh: boolean = false): Promise<InventoryType[]> {
    return getMasterDataWithOfflineSupport<InventoryType>('inventoryTypes', '/api/v1/gaushala/master/inventory-types', forceRefresh);
  },

  /**
   * Get all inventory units with offline support
   */
  async getAllInventoryUnits(forceRefresh: boolean = false): Promise<InventoryUnit[]> {
    return getMasterDataWithOfflineSupport<InventoryUnit>('inventoryUnits', '/api/v1/gaushala/inventory/units', forceRefresh);
  },

  /**
   * Prefetch all master data (useful on app start when online)
   */
  async prefetchAll(): Promise<void> {
    if (!navigator.onLine) {
      console.log('[OfflineMasterData] Offline - skipping prefetch');
      return;
    }

    console.log('[OfflineMasterData] Prefetching all master data...');

    try {
      await Promise.all([
        this.getAllBreeds(true),
        this.getAllSpecies(true),
        this.getAllGenders(true),
        this.getAllColors(true),
        this.getAllLocations(true),
        this.getAllSources(true),
        this.getAllOwnership(true),
        this.getAllFeedTypes(true),
        this.getAllInventoryTypes(true),
        this.getAllInventoryUnits(true),
      ]);
      console.log('[OfflineMasterData] Prefetch complete');
    } catch (error) {
      console.error('[OfflineMasterData] Prefetch failed:', error);
    }
  },

  /**
   * Check if master data is cached
   */
  async isCached(key: MasterDataKey): Promise<boolean> {
    return isMasterDataCached(key);
  },

  /**
   * Check if all master data is cached
   */
  async isAllCached(): Promise<boolean> {
    const checks = await Promise.all(
      MASTER_DATA_KEYS.map(key => isMasterDataCached(key))
    );
    return checks.every(cached => cached);
  },

  /**
   * Get master data names by IDs (useful for display)
   */
  async getBreedName(id: number): Promise<string> {
    const breeds = await this.getAllBreeds();
    const breed = breeds.find(b => b.id === id);
    return breed?.name || `Breed #${id}`;
  },

  async getSpeciesName(id: number): Promise<string> {
    const species = await this.getAllSpecies();
    const sp = species.find(s => s.id === id);
    return sp?.name || `Species #${id}`;
  },

  async getGenderName(id: number): Promise<string> {
    const genders = await this.getAllGenders();
    const gender = genders.find(g => g.id === id);
    return gender?.name || `Gender #${id}`;
  },

  async getColorName(id: number): Promise<string> {
    const colors = await this.getAllColors();
    const color = colors.find(c => c.id === id);
    return color?.name || `Color #${id}`;
  },
};

export default offlineMasterDataApi;
